import { Logo } from '@/components/logo';
import { createPortal } from 'react-dom';
import { NavLink, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  Inbox,
  Settings,
  Plus,
  Filter,
  User,
  Briefcase,
  Mail,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  Inbox as InboxIcon,
  Eye,
  ShieldAlert,
  Send,
  Trash2,
  MailX,
  FileEdit } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { useMailStore } from '@/stores/mail';
import { useUIStore } from '@/stores/ui';
import { listMailboxes } from '@/api/mailboxes';
import { createFilter } from '@/api/filters';
import { getInboxCounts } from '@/api/inbox';
import { cn } from '@/lib/utils';

export function Sidebar() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { sidebarOpen, toggleSidebar, mobileSidebarOpen, setMobileSidebarOpen, theme, toggleTheme } = useUIStore();
  const { mailboxes, setSelectedMailbox } = useMailStore();
  const queryClient = useQueryClient();
  const location = useLocation();

  // On mobile, selecting anything that navigates should close the drawer so the
  // chosen page is visible (was persisting over the content).
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname, location.search, setMobileSidebarOpen]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [controlsOpen, setControlsOpen] = useState(false); // collapsible app controls (settings/theme/sign-out)
  const [filterKw, setFilterKw] = useState('');
  const [filterField, setFilterField] = useState<'sender' | 'subject' | 'body' | 'any'>('subject');
  const addSpamFilter = useMutation({
    mutationFn: () => createFilter({ listType: 'block', matchType: 'keyword', field: filterField, matchValue: filterKw.trim() }),
    onSuccess: () => { setFilterKw(''); setFilterOpen(false); queryClient.invalidateQueries({ queryKey: ['filters'] }); }
  });

  const { data } = useQuery({
    queryKey: ['mailboxes'],
    queryFn: listMailboxes,
    enabled: !!user
  });

  useEffect(() => {
    if (data?.mailboxes) {
      useMailStore.setState({ mailboxes: data.mailboxes });
    }
  }, [data]);

  const personal = mailboxes.filter((m) => m.category === 'personal');
  const business = mailboxes.filter((m) => m.category === 'business');

  const handleNavigate = (fn: () => void) => {
    fn();
    setMobileSidebarOpen(false);
  };

  // ---- Shared nav content ----
  const navContent = (
    <>
      <div className="px-3 py-2">
        <button
          onClick={() => navigate('/compose')}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-bmail-goldfill px-4 py-2.5 font-semibold text-black hover:bg-bmail-goldfill-hover transition-colors border-2 border-black"
        >
          <Plus size={18} /> Compose
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-2">
        <UnifiedGroup />

        {personal.length > 0 && (
          <>
            <div className="mt-6 mb-2 px-2 text-xs font-semibold uppercase tracking-wider text-bmail-text-secondary">
              Personal
            </div>
            {personal.map((m) => (
              <MailboxGroup key={m.id} mailbox={m} onClick={() => setSelectedMailbox(m)} />
            ))}
          </>
        )}

        {business.length > 0 && (
          <>
            <div className="mt-6 mb-2 px-2 text-xs font-semibold uppercase tracking-wider text-bmail-text-secondary">
              <Briefcase size={12} className="inline mr-1" /> Business
            </div>
            {business.map((m) => (
              <MailboxGroup key={m.id} mailbox={m} onClick={() => setSelectedMailbox(m)} />
            ))}
          </>
        )}
      </nav>

      <div className="border-t border-bmail-border p-3">
        <button
          onClick={() => navigate('/onboarding')}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-bmail-text hover:bg-bmail-surface-hover"
        >
          <Plus size={16} /> Add mailbox
        </button>
        <button
          onClick={() => setFilterOpen(true)}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-bmail-text hover:bg-bmail-surface-hover"
        >
          <Filter size={16} /> Add spam filter
        </button>
        {filterOpen && createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setFilterOpen(false)}>
            <div className="w-full max-w-sm rounded-xl border border-bmail-border bg-bmail-surface p-4" onClick={(e) => e.stopPropagation()}>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-semibold text-bmail-text">Add spam filter</h3>
                <button onClick={() => setFilterOpen(false)} className="text-bmail-text-secondary hover:text-bmail-text"><X size={18} /></button>
              </div>
              <label className="mb-2 block text-sm text-bmail-text">Send to Spam when…</label>
              <div className="flex flex-col gap-2">
                <select
                  value={filterField}
                  onChange={(e) => setFilterField(e.target.value as typeof filterField)}
                  className="rounded-lg border border-bmail-border bg-bmail-surface px-3 py-2 text-sm text-bmail-text focus:outline-none focus:ring-2 focus:ring-bmail-gold"
                >
                  <option value="sender">Sender</option>
                  <option value="subject">Subject</option>
                  <option value="body">Body text</option>
                  <option value="any">Anywhere</option>
                </select>
                <input
                  autoFocus
                  value={filterKw}
                  onChange={(e) => setFilterKw(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && filterKw.trim()) addSpamFilter.mutate(); }}
                  placeholder="contains this keyword…"
                  className="rounded-lg border border-bmail-border bg-bmail-surface px-3 py-2 text-sm text-bmail-text focus:outline-none focus:ring-2 focus:ring-bmail-gold"
                />
                <button
                  disabled={!filterKw.trim() || addSpamFilter.isPending}
                  onClick={() => addSpamFilter.mutate()}
                  className="rounded-lg bg-bmail-goldfill px-4 py-2 text-sm font-medium text-bmail-on-accent hover:bg-bmail-goldfill-hover disabled:opacity-50"
                >
                  Add filter
                </button>
              </div>
              <p className="mt-2 text-xs text-bmail-text-secondary">Manage all filters in Settings → Spam filters.</p>
            </div>
          </div>,
          document.body
        )}
        {/* Collapsible app controls — accordion keeps the sidebar footer compact
            on mobile, so nav/mailboxes stay reachable without scrolling. */}
        <button
          onClick={() => setControlsOpen((v) => !v)}
          aria-expanded={controlsOpen}
          className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm text-bmail-text hover:bg-bmail-surface-hover"
        >
          <span className="flex items-center gap-2"><User size={16} /> Account</span>
          <ChevronDown
            size={14}
            className={cn('text-bmail-text-secondary transition-transform', controlsOpen && 'rotate-180')}
          />
        </button>
        {controlsOpen && (
          <div className="mb-1">
            <button
              onClick={() => navigate('/settings')}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 pl-6 text-sm text-bmail-text hover:bg-bmail-surface-hover"
            >
              <Settings size={16} /> Settings
            </button>
            <button
              onClick={toggleTheme}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 pl-6 text-sm text-bmail-text hover:bg-bmail-surface-hover"
            >
              {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
            </button>
            <button
              onClick={() => { logout(); navigate('/login'); }}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 pl-6 text-sm text-bmail-error hover:bg-bmail-error/10"
            >
              <User size={16} /> Sign out
            </button>
          </div>
        )}
      </div>
    </>
  );

  // ---- Mobile sidebar (off-canvas drawer) ----
  const mobileSidebar = (
    <>
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 transition-opacity md:hidden',
          mobileSidebarOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={() => setMobileSidebarOpen(false)}
      />
      <aside
        className={cn(
          'fixed left-0 top-0 z-50 flex h-full w-72 flex-col border-r border-bmail-border bg-bmail-surface text-bmail-text transition-transform duration-200 md:hidden',
          mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center justify-between px-4 py-4">
          <Logo className="h-11 w-auto shrink-0" />
          <button onClick={() => setMobileSidebarOpen(false)} className="rounded-lg p-1.5 text-bmail-text-secondary hover:bg-bmail-surface-hover">
            <X size={20} />
          </button>
        </div>
        {navContent}
      </aside>
    </>
  );

  // ---- Desktop sidebar ----
  if (!sidebarOpen) {
    return (
      <>
        {mobileSidebar}
        <div className="hidden w-14 flex-col items-center border-r border-bmail-border bg-bmail-surface py-4 md:flex">
          <button onClick={toggleSidebar} className="rounded-lg p-2 text-bmail-text-secondary hover:bg-bmail-surface-hover">
            <ChevronRight size={20} />
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {mobileSidebar}
      <aside className="hidden w-72 flex-col border-r border-bmail-border bg-bmail-surface text-bmail-text md:flex">
        <div className="flex items-center justify-between px-4 py-4">
          <Logo className="h-11 w-auto shrink-0" />
          <button onClick={toggleSidebar} className="rounded-lg p-1.5 text-bmail-text-secondary hover:bg-bmail-surface-hover">
            <ChevronLeft size={18} />
          </button>
        </div>
        {navContent}
      </aside>
    </>
  );
}

function SidebarItem({ to, icon, label, badge }: { to: string; icon: React.ReactNode; label: string; badge?: number }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        cn(
          'flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive ? 'bg-bmail-surface-hover text-bmail-gold' : 'text-bmail-text hover:bg-bmail-surface-hover hover:text-bmail-text'
        )
      }
    >
      <span className="flex items-center gap-3">
        {icon}
        {label}
      </span>
      {badge ? <span className="rounded-full bg-bmail-goldfill px-2 py-0.5 text-xs text-black">{badge}</span> : null}
    </NavLink>
  );
}

// ---- Unified mailbox: collapsible accordion over the cross-account views ----
function UnifiedGroup() {
  const [expanded, setExpanded] = useState(true);

  // Fetch review/spam counts for badges. Refetch every 60s and on window focus
  // so the user sees updates without a manual refresh.
  const { data: counts } = useQuery({
    queryKey: ['inbox-counts'],
    queryFn: getInboxCounts,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true
  });

  const reviewBadge = counts?.novelSenders || 0;
  const spamBadge = counts?.spam || 0;

  const items = [
    { to: '/inbox', label: 'Inbox', icon: <InboxIcon size={14} />, end: true, badge: 0, badgeClass: '' },
    { to: '/inbox/sent', label: 'Sent', icon: <Send size={14} />, end: false, badge: 0, badgeClass: '' },
    { to: '/inbox/drafts', label: 'Drafts', icon: <FileEdit size={14} />, end: false, badge: 0, badgeClass: '' },
    { to: '/inbox/review', label: 'Might be spam', icon: <Eye size={14} />, end: false, badge: reviewBadge, badgeClass: 'bg-bmail-warning text-bmail-navy' },
    { to: '/inbox/spam', label: 'Definitely spam', icon: <ShieldAlert size={14} />, end: false, badge: spamBadge, badgeClass: 'bg-bmail-error text-white' },
    { to: '/inbox/deleted', label: 'Deleted', icon: <Trash2 size={14} />, end: false, badge: 0, badgeClass: '' }
  ];
  return (
    <div className="mb-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          'flex w-full items-center justify-between gap-1 rounded-lg px-2 py-2 text-sm transition-colors',
          expanded ? 'bg-bmail-surface-hover text-bmail-gold' : 'text-bmail-text hover:bg-bmail-surface-hover'
        )}
      >
        <span className="flex items-center gap-2"><InboxIcon size={16} /> Unified Mailboxes</span>
        <ChevronDown size={14} className={cn('transition-transform', expanded ? 'rotate-180' : '')} />
      </button>
      {expanded && (
        <div className="ml-4 mt-0.5 border-l border-bmail-border pl-2">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                cn(
                  'flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-xs transition-colors',
                  isActive ? 'text-bmail-gold bg-bmail-surface-hover' : 'text-bmail-text-secondary hover:bg-bmail-surface-hover hover:text-bmail-text'
                )
              }
            >
              {it.icon}
              {it.label}
              {it.badge > 0 && (
                <span className={cn('ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none', it.badgeClass)}>
                  {it.badge}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Mailbox group with expandable sub-folders ----
// Whole row toggles accordion. Folders use query params to filter messages.
function MailboxGroup({ mailbox, onClick }: { mailbox: any; onClick: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id, displayName, email } = mailbox;

  // Determine which folder is currently active based on query params
  const currentFolder = searchParams.get('folder') || 'inbox';

  // Naming + order kept consistent with the Unified Mailboxes accordion.
  const folders = [
    { folder: 'inbox', label: 'Inbox', icon: <Inbox size={14} />, path: `/inbox/${id}` },
    { folder: 'sent', label: 'Sent', icon: <Send size={14} />, path: `/inbox/${id}?folder=sent` },
    { folder: 'maybe-spam', label: 'Might be spam', icon: <Eye size={14} />, path: `/inbox/${id}?folder=maybe-spam` },
    { folder: 'spam', label: 'Definitely spam', icon: <ShieldAlert size={14} />, path: `/inbox/${id}?folder=spam` },
    { folder: 'deleted', label: 'Deleted', icon: <Trash2 size={14} />, path: `/inbox/${id}?folder=deleted` },
  ];

  const handleClickAccount = () => {
    setExpanded(!expanded);
    onClick();
  };

  return (
    <div className="mb-1">
      {/* Account row — whole row toggles accordion */}
      <div
        className={cn(
          'flex w-full items-center gap-1 rounded-lg px-2 py-2 text-sm transition-colors',
          expanded ? 'bg-bmail-surface-hover' : 'hover:bg-bmail-surface-hover'
        )}
      >
        <button
          onClick={(e) => { e.stopPropagation(); navigate(`/compose?from=${id}`); }}
          title={`New email from ${email}`}
          aria-label={`New email from ${email}`}
          className="shrink-0 rounded p-1 text-bmail-text-secondary hover:bg-bmail-surface-hover hover:text-bmail-gold"
        >
          <Mail size={14} />
        </button>
        <button
          onClick={handleClickAccount}
          className={cn(
            'flex min-w-0 flex-1 items-center justify-between gap-1 text-left',
            expanded ? 'text-bmail-gold' : 'text-bmail-text-secondary hover:text-bmail-text'
          )}
        >
          <span className="truncate">{displayName || email}</span>
          <span className="flex items-center gap-1">
            {!mailbox.isActive && <span className="text-xs text-bmail-text-secondary">paused</span>}
            <ChevronDown size={14} className={cn('transition-transform', expanded ? 'rotate-180' : '')} />
          </span>
        </button>
      </div>

      {expanded && (
        <div className="ml-4 mt-0.5 border-l border-bmail-border pl-2">
          {folders.map((f) => {
            const isActive = currentFolder === f.folder;
            return (
              <button
                key={f.folder}
                onClick={() => {
                  onClick();
                  navigate(f.path);
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-xs transition-colors',
                  isActive ? 'text-bmail-gold bg-bmail-surface-hover' : 'text-bmail-text-secondary hover:bg-bmail-surface-hover hover:text-bmail-text'
                )}
              >
                {f.icon}
                {f.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}